import React, { useState } from "react";
import { MessageSquare, Send, Car, Save } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  nou: "bg-blue-100 text-blue-700",
  in_verificare: "bg-yellow-100 text-yellow-700",
  acceptat: "bg-emerald-100 text-emerald-700",
  in_lucru: "bg-purple-100 text-purple-700",
  finalizat: "bg-green-100 text-green-700",
  respins: "bg-red-100 text-red-700"
};

const STATUS_LABELS = { nou: "Nou", in_verificare: "In verificare", acceptat: "Acceptat", in_lucru: "In lucru", finalizat: "Finalizat", respins: "Respins" };

const ServiciiTags = ({ servicii }) => {
  if (!servicii || servicii.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2">
      <span className="text-sm text-gray-500">Servicii:</span>
      {servicii.map((s) => (
        <span key={s} className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">{s.replace(/_/g, " ")}</span>
      ))}
    </div>
  );
};

const NoteItem = ({ note }) => (
  <div className="p-3 bg-blue-50 rounded-lg text-sm">
    <div className="flex justify-between text-xs text-gray-500 mb-1">
      <span className="font-medium">{note.autor}</span>
      <span>{note.data ? note.data.slice(0, 10) : ""}</span>
    </div>
    <p>{note.text}</p>
  </div>
);

const VehicleSection = ({ dosar, token, onUpdated }) => {
  const [nrAuto, setNrAuto] = useState(dosar.nr_auto || "");
  const [marcaModel, setMarcaModel] = useState(dosar.marca_model || "");
  const [asigurator, setAsigurator] = useState(dosar.asigurator || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/consilier/dosare/${dosar.id}/vehicle`, {
        nr_auto: nrAuto, marca_model: marcaModel, asigurator: asigurator
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Date vehicul salvate!");
      if (onUpdated) onUpdated();
    } catch {
      toast.error("Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-t pt-4">
      <h3 className="font-semibold mb-3 flex items-center gap-2"><Car className="w-4 h-4" />Date Vehicul</h3>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Nr. Auto</label>
          <input value={nrAuto} onChange={e => setNrAuto(e.target.value.toUpperCase())} placeholder="B 123 ABC" className="w-full px-3 py-2 border rounded-lg text-sm font-mono" data-testid="vehicle-nr-auto" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Marca / Model</label>
          <input value={marcaModel} onChange={e => setMarcaModel(e.target.value)} placeholder="Ex: BMW X5" className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="vehicle-marca-model" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Asigurator</label>
          <input value={asigurator} onChange={e => setAsigurator(e.target.value)} placeholder="Ex: Euroins" className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="vehicle-asigurator" />
        </div>
      </div>
      <button onClick={handleSave} disabled={saving} className="mt-3 flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm hover:bg-emerald-600 disabled:opacity-50" data-testid="vehicle-save-btn">
        <Save className="w-4 h-4" />{saving ? "Se salveaza..." : "Salveaza date vehicul"}
      </button>
    </div>
  );
};

const ConsilierDosarModal = ({ dosar, onClose, token, onAddNote, onAddVisibleNote, onUpdated }) => {
  const [noteText, setNoteText] = useState("");
  const [visibleNoteText, setVisibleNoteText] = useState("");
  const notes = dosar.note_interne || [];
  const visibleNotes = dosar.note_vizibile || [];
  const servicii = dosar.servicii_solicitate || [];

  const handleAddNote = () => {
    if (!noteText.trim()) return;
    onAddNote(dosar.id, noteText);
    setNoteText("");
  };

  const handleAddVisibleNote = () => {
    if (!visibleNoteText.trim()) return;
    onAddVisibleNote(dosar.id, visibleNoteText);
    setVisibleNoteText("");
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="dosar-detail-modal">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">{dosar.numar_dosar}</h2>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[dosar.status]}`}>{STATUS_LABELS[dosar.status]}</span>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><span className="text-gray-500">Client:</span> <span className="font-medium">{dosar.nume_prenume}</span></div>
            <div><span className="text-gray-500">Telefon:</span> <span className="font-medium">{dosar.telefon}</span></div>
            <div><span className="text-gray-500">Email:</span> <span>{dosar.email || "-"}</span></div>
            <div><span className="text-gray-500">Oras:</span> <span>{dosar.oras || "-"}</span></div>
            <div><span className="text-gray-500">Tip dauna:</span> <span>{dosar.tip_dauna || "-"}</span></div>
            <div><span className="text-gray-500">Cod afiliat:</span> <span>{dosar.cod_afiliat || "-"}</span></div>
            {dosar.suma_despagubire && <div><span className="text-gray-500">Suma despagubire:</span> <span className="font-bold text-emerald-600">{dosar.suma_despagubire} RON</span></div>}
            {dosar.discount > 0 && <div><span className="text-gray-500">Discount:</span> <span className="font-bold text-amber-600">{dosar.discount}%</span></div>}
          </div>

          {(dosar.nr_auto || dosar.marca_model || dosar.asigurator) && (
            <div className="grid grid-cols-3 gap-4 text-sm p-3 bg-gray-50 rounded-lg">
              <div><span className="text-gray-500">Nr. Auto:</span> <span className="font-mono font-medium">{dosar.nr_auto || "-"}</span></div>
              <div><span className="text-gray-500">Marca/Model:</span> <span className="font-medium">{dosar.marca_model || "-"}</span></div>
              <div><span className="text-gray-500">Asigurator:</span> <span className="font-medium">{dosar.asigurator || "-"}</span></div>
            </div>
          )}

          {dosar.detalii && <div className="p-3 bg-gray-50 rounded-lg text-sm"><span className="text-gray-500">Detalii:</span> {dosar.detalii}</div>}

          <ServiciiTags servicii={servicii} />

          <VehicleSection dosar={dosar} token={token} onUpdated={onUpdated} />

          <div className="border-t pt-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><MessageSquare className="w-4 h-4" />Note Interne</h3>
            <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">
              {notes.length === 0 ? (
                <p className="text-sm text-gray-400">Nicio nota.</p>
              ) : (
                notes.map((n, i) => <NoteItem key={`${n.data}-${n.autor}-${i}`} note={n} />)
              )}
            </div>
            <div className="flex gap-2">
              <input value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Adauga o nota interna..." className="flex-1 px-3 py-2 border rounded-lg text-sm" data-testid="note-input" onKeyDown={e => e.key === "Enter" && handleAddNote()} />
              <button onClick={handleAddNote} className="px-3 py-2 bg-emerald-500 text-white rounded-lg" data-testid="note-submit"><Send className="w-4 h-4" /></button>
            </div>
          </div>

          <div className="border-t pt-4">
            <h3 className="font-semibold mb-1 flex items-center gap-2"><MessageSquare className="w-4 h-4 text-amber-500" />Note Vizibile Client</h3>
            <p className="text-xs text-gray-400 mb-3">Ultima nota apare in pagina de verificare status dosar (doar cand statusul e "In lucru")</p>
            <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">
              {visibleNotes.length === 0 ? (
                <p className="text-sm text-gray-400">Nicio nota vizibila.</p>
              ) : (
                visibleNotes.map((n, i) => (
                  <div key={`vis-${n.data}-${i}`} className="p-3 bg-amber-50 border border-amber-100 rounded-lg text-sm">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span className="font-medium">{n.autor}</span>
                      <span>{n.data ? n.data.slice(0, 10) : ""}</span>
                    </div>
                    <p>{n.text}</p>
                  </div>
                ))
              )}
            </div>
            <div className="flex gap-2">
              <input value={visibleNoteText} onChange={e => setVisibleNoteText(e.target.value)} placeholder="Nota vizibila pentru client..." className="flex-1 px-3 py-2 border border-amber-200 rounded-lg text-sm bg-amber-50/50" data-testid="visible-note-input" onKeyDown={e => e.key === "Enter" && handleAddVisibleNote()} />
              <button onClick={handleAddVisibleNote} className="px-3 py-2 bg-amber-500 text-white rounded-lg" data-testid="visible-note-submit"><Send className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsilierDosarModal;
